import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Typography, Button } from 'antd';
import { getContentWrapperStyle } from '../templateStyles';

const { Title, Paragraph } = Typography;

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function darkenColor(hex, amount = 0.2) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

function Countdown({ target, t }) {
  const [left, setLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const diff = new Date(target) - new Date();
      if (diff <= 0) { setLeft(t('sections.cta.expired')); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setLeft(`${d > 0 ? d + ` ${t('sections.cta.days')} ` : ''}${h} ${t('sections.cta.hours')} ${m} ${t('sections.cta.minutes')}`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [target, t]);
  return <span style={{ fontWeight: 700, fontSize: 18 }}>{left}</span>;
}

export default function SectionCTA({ slug, section, siteInfo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = section.config || {};
  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const buttonText = config.button_text || t('sections.cta.defaultButton');
  const buttonLink = config.button_link || 'contact';
  const bgColor = config.background_color || brandColor;

  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';
  const contentFont = getContentWrapperStyle(template, brandColor);
  const lum = getLuminance(bgColor);
  const darkBg = lum > 0.55 ? darkenColor(bgColor, 0.3) : bgColor;

  const sectionBg = isMinimal
    ? '#fff'
    : isClassic
    ? `linear-gradient(180deg, ${darkBg}, ${darkenColor(darkBg, 0.1)})`
    : `linear-gradient(135deg, ${darkBg}, ${darkenColor(darkBg, 0.15)})`;

  const textColor = isMinimal ? '#1a1a2e' : '#fff';
  const subColor = isMinimal ? '#555' : 'rgba(255,255,255,0.9)';

  const btnBg = isMinimal ? bgColor : '#fff';
  const btnColor = isMinimal ? '#fff' : darkBg;
  const btnBorder = isMinimal ? `2px solid ${bgColor}` : 'none';
  const btnRadius = isMinimal ? 0 : isClassic ? 4 : 24;

  return (
    <div style={{
      background: sectionBg,
      padding: '48px 24px',
      textAlign: 'center',
      color: textColor,
      borderTop: isMinimal ? `3px solid ${bgColor}` : 'none',
      borderBottom: isMinimal ? `3px solid ${bgColor}` : 'none',
      fontFamily: contentFont.fontFamily,
    }}>
      <Title level={2} style={{
        color: textColor,
        marginBottom: 8,
        fontWeight: isMinimal ? 300 : 600,
        letterSpacing: isMinimal ? 2 : 0,
      }}>
        {section.title || t('sections.cta.title')}
      </Title>
      {section.subtitle && (
        <Paragraph style={{ color: subColor, fontSize: 16, marginBottom: 24 }}>
          {section.subtitle}
        </Paragraph>
      )}
      {config.countdown_to && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, marginBottom: 4, opacity: 0.8 }}>{t('sections.cta.countdownLabel')}</div>
          <Countdown target={config.countdown_to} t={t} />
        </div>
      )}
      <Button
        size="large"
        onClick={() => {
          if (buttonLink.startsWith('#')) {
            const el = document.getElementById(buttonLink.slice(1)) || document.getElementById(`section-${buttonLink.slice(1)}`);
            if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
          }
          navigate(`/s/${slug}/${buttonLink}`);
        }}
        style={{
          background: btnBg,
          color: btnColor,
          border: btnBorder,
          borderRadius: btnRadius,
          fontWeight: 600,
          height: 48,
          padding: '0 32px',
          fontSize: 16,
        }}
      >
        {buttonText}
      </Button>
    </div>
  );
}
